import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Play, ExternalLink } from "lucide-react";
import { gameCredits, gameProjects, albums, spotifyOpenLink } from "../data/mock";
import AlbumCard from "../components/AlbumCard";
import GameAudioReel from "../components/GameAudioReel";
import SoundDesignShowcase from "../components/SoundDesignShowcase";
import { usePlayer } from "../context/PlayerContext";
import { Reveal } from "../hooks/useReveal";

const ProjectPoster = ({ project }) => {
  const { play, current } = usePlayer();
  const isPlaying = current?.id === project.id;
  return (
    <div className="group relative">
      <button
        onClick={() => play(project)}
        className="block w-full text-left focus:outline-none"
        aria-label={`Play ${project.title}`}
      >
        <div className="aspect-[4/5] overflow-hidden rounded-xl shadow-xl relative bg-neutral-900">
          <img
            src={project.poster}
            alt={project.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
          {/* play button */}
          <div className="absolute right-4 top-4 flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/85 bg-black/40 backdrop-blur px-2.5 py-1 rounded-full">
              {project.label}
            </span>
          </div>
          <div
            className={`absolute right-5 bottom-5 w-14 h-14 rounded-full bg-white text-black flex items-center justify-center shadow-2xl ${
              isPlaying
                ? "scale-100 opacity-100"
                : "translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100"
            } transition-all duration-300`}
          >
            <Play className="w-6 h-6 ml-0.5" fill="currentColor" />
          </div>
          <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 text-white">
            <h3 className="text-2xl md:text-3xl font-semibold tracking-tight">
              {project.title}
            </h3>
            <p className="text-white/75 text-sm mt-1">{project.subtitle}</p>
          </div>
        </div>
      </button>
      {/* External Spotify link */}
      <a
        href={spotifyOpenLink(project.id, project.spotifyType || "album")}
        target="_blank"
        rel="noreferrer"
        className="mt-3 inline-flex items-center gap-2 text-sm text-neutral-700 hover:text-black"
      >
        Open in Spotify <ExternalLink className="w-3.5 h-3.5" />
      </a>
    </div>
  );
};

const GameCredits = () => {
  const featuredProjects = [
    ...gameProjects.filter((p) => !["Cactus Jam", "Angel Earth Edition"].includes(p.title)),
    ...albums.filter((a) => ["35sv6h4QXM5p2RpUFPF6zu", "1vZcCwZK9u6mgDJX97YwN8"].includes(a.id)).map((a) => ({ ...a, poster: a.cover, label: "Soundtrack", spotifyType: "album" })),
  ];
  const otherGameAlbums = albums.filter(
    (a) => a.category === "games" && !featuredProjects.some((p) => p.id === a.id)
  );

  return (
    <div className="bg-[#f4f1ec] text-black">
      {/* Hero */}
      <section className="pt-28 md:pt-32 pb-8 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-70"
          style={{
            background:
              "radial-gradient(50% 60% at 15% 30%, rgba(255,176,64,0.18), transparent 60%), radial-gradient(40% 60% at 85% 70%, rgba(50,180,170,0.14), transparent 60%)",
          }}
        />
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 text-center relative">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-amber-700">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              Game &amp; VR Credits
            </span>
          </Reveal>
          <Reveal delay={80}>
            <h1
              className="mt-4 text-4xl md:text-6xl lg:text-7xl tracking-tight font-semibold leading-[1.04]"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Cinematic Composer for <br className="hidden md:block" />
              Video Games &amp; VR
            </h1>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-5 text-neutral-700 text-base md:text-lg tracking-wide">
              Meta Top 5 VR Games &middot; Adaptive game audio &middot;
              Cinematic scoring
            </p>
          </Reveal>
        </div>
      </section>

      <GameAudioReel onCreditsPage />
      <SoundDesignShowcase />
      {/* Featured projects with playable posters */}
      <section className="pb-10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="flex items-end justify-between mb-6 md:mb-8 flex-wrap gap-3">
              <h2
                className="text-2xl md:text-4xl font-medium tracking-tight"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Featured projects
              </h2>
              <p className="text-sm text-neutral-500">
                Click any poster to play the soundtrack
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {featuredProjects.map((p, i) => (
              <Reveal key={p.title} delay={i * 60}>
                <ProjectPoster project={p} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Additional game albums */}
      {otherGameAlbums.length > 0 && (
        <section className="py-10">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <Reveal>
              <h2
                className="text-2xl md:text-3xl font-medium tracking-tight mb-6"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                More game &amp; VR soundtracks
              </h2>
            </Reveal>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
              {otherGameAlbums.map((a, i) => (
                <Reveal key={a.id} delay={i * 60}>
                  <AlbumCard album={a} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Studio gallery */}
      <section className="py-10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <Reveal>
            <h2
              className="text-2xl md:text-3xl font-medium tracking-tight mb-6"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Studio &amp; capture gallery
            </h2>
          </Reveal>
          <div className="columns-2 md:columns-3 gap-4 [column-fill:_balance]">
            {[...gameCredits.gallery, "/images/ug-vol2.jpg", "/images/barber.jpg"].map((src, i) => (
              <div
                key={i}
                className="mb-4 break-inside-avoid overflow-hidden rounded-md shadow-sm group"
              >
                <img
                  src={src}
                  alt={`Game art ${i + 1}`}
                  className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight mb-6">More Game Projects Coming Soon</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[["R-01", "Composer", "r01"], ["Crownless: Reignfall", "Composer", "crownless"], ["Sea Monkeys", "Original Soundtrack", "sea-monkeys"], ["Dungeon! Order of the Raven", "Audio Director", "dungeon"]].map(([title, role, image]) => (
              <article key={title}>
                <img src={`/images/${image}.jpg`} alt={title} className="w-full rounded-md shadow-sm" loading="lazy" />
                <h3 className="mt-3 font-semibold">{title}</h3>
                <p className="text-sm text-neutral-600">{role}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Nav */}
      <section className="py-10 border-t border-black/10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 flex items-center justify-between">
          <Link
            to="/work"
            className="inline-flex items-center gap-2 text-neutral-700 hover:text-black transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Work
          </Link>
          <Link
            to="/work/film-credits"
            className="group inline-flex items-center gap-2 text-black font-medium"
          >
            <span>
              <span className="block text-xs uppercase tracking-[0.18em] text-neutral-500">
                Next
              </span>
              Music to Film
            </span>
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default GameCredits;
